/* DEBACQ / Judge.me presentation adapter. No requests, submissions or review
   data are intercepted. Existing app event handlers and theme files stay intact. */
(() => {
  'use strict';
  if (window.debacqJudgeTheme) { window.debacqJudgeTheme.scan(); return; }

  const ROOT = '[data-debacq-reviews]';
  const FORM = '.jdgm-write-review-modal';
  // The current photo viewer uses jdgm-widget-revamp, not jdgm-widget.
  const PHOTO = '.jdgm-modal.jdgm-widget-revamp, .jdgm-modal.jdgm-widget';
  const MODAL = `${FORM}, ${PHOTO}`;
  const controllers = new Map();
  const surfaces = new Map();
  const originalGlyphs = new WeakMap();
  let productObserver;
  let observedProductInfo;
  let productFrame = 0;
  const linkedRatings = new WeakMap();

  function selectIncluding(root, selector) {
    return [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  }
  function setAttribute(node, name, value) {
    if (node.getAttribute(name) !== value) node.setAttribute(name, value);
  }
  function normalText(node) { return (node?.textContent || '').replace(/\s+/g, ' ').trim(); }
  function readNumber(text) {
    const match = String(text || '').match(/(?:^|\D)([0-5](?:[.,]\d+)?)(?=\D|$)/);
    if (!match) return null;
    const number = Number(match[1].replace(',', '.'));
    return Number.isFinite(number) && number >= 0 && number <= 5 ? number : null;
  }
  function ratingState(rating, index) {
    rating = Math.round(Number(rating) * 10) / 10;
    if (index < Math.floor(rating)) return 'full';
    if (index > Math.floor(rating)) return 'empty';
    const fraction = Math.round((rating % 1) * 100) / 100;
    return fraction > .7 ? 'full' : fraction >= .3 ? 'half' : 'empty';
  }
  function seamlessReviewStar(svg, state) {
    if (!svg || !['full', 'half', 'empty'].includes(state)) return svg;
    const left = 'M6 0v8.635L2.292 11 3.48 6.87 0 4.202l4.443-.187L6 0Z';
    const right = 'M6 0v8.635L9.708 11 8.52 6.87 12 4.202l-4.443-.187L6 0Z';
    const compound = left + right.replace(/^M6 0/, 'm0 0');
    const paths = [...svg.querySelectorAll('path')];
    const isNative = state === 'half'
      ? paths.length === 2 && paths[0].getAttribute('d') === left && paths[1].getAttribute('d') === right
      : paths.length === 1 && paths[0].getAttribute('d') === compound;
    if (!isNative) return svg; // A future, different theme icon is not rewritten.

    // Same 12 x 11 silhouette/vertices as the theme. One perimeter instead of
    // two adjacent closed halves removes an internal antialiasing edge on iOS.
    const outline = 'M6 0L7.557 4.015L12 4.202L8.52 6.87L9.708 11L6 8.635L2.292 11L3.48 6.87L0 4.202L4.443 4.015Z';
    const copy = svg.cloneNode(true);
    const base = paths[0].cloneNode(true);
    base.setAttribute('d', outline);
    if (state === 'half') base.setAttribute('fill-opacity', paths[1].getAttribute('fill-opacity') || '0.4');
    copy.replaceChildren(base);
    // The translucent silhouette continues UNDER the opaque half, so there is
    // no transparent gap at their boundary. Half-star logic/colours stay native.
    if (state === 'half') copy.append(paths[0].cloneNode(true));
    return copy;
  }
  function svgUrl(svg) {
    const copy = svg.cloneNode(true);
    copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    return `url("data:image/svg+xml,${encodeURIComponent(new XMLSerializer().serializeToString(copy))}")`;
  }

  function readConfig(root) {
    try { return JSON.parse(root.querySelector('[data-debacq-review-config]').textContent); }
    catch (error) { console.warn('DEBACQ reviews: invalid integration settings.', error); return null; }
  }
  function setStyle(node, name, value) {
    if (node.style.getPropertyValue(name) !== value) node.style.setProperty(name, value);
  }
  function nativeVariables(controller, surface) {
    const button = controller.root.querySelector('[data-debacq-review-probes] .button');
    const nativeStars = document.querySelector('.shopify-section--main-product [data-block-type="rating"] .rating-badge__stars') ||
      controller.root.querySelector('[data-debacq-review-probes] .rating-badge__stars');
    if (!button) return;
    const b = getComputedStyle(button);
    const properties = {
      '--dbq-review-button-padding': b.padding,
      '--dbq-review-button-transition': b.transition,
      '--dbq-review-star-gap': nativeStars ? getComputedStyle(nativeStars).columnGap : '.125rem'
    };
    ['full', 'half', 'empty'].forEach(state => {
      if (controller.icons[state]) properties[`--dbq-review-star-${state}`] = svgUrl(controller.icons[state]);
    });
    Object.entries(properties).forEach(([name, value]) => setStyle(surface, name, value));
    if (surface === controller.root) updateScrollSpacing(controller);
  }
  function updateScrollSpacing(controller) {
    // theme.css already sets root scroll-padding for the sticky header.
    // Add only the product bar that sits below it on desktop, never the header twice.
    const bar = document.querySelector('product-sticky-bar');
    const style = bar && getComputedStyle(bar);
    const topBar = style && style.position === 'fixed' && style.top !== 'auto' &&
      window.matchMedia('(min-width: 700px)').matches;
    setStyle(controller.root, '--dbq-review-sticky-bar-height', `${topBar ? Math.ceil(bar.getBoundingClientRect().height) : 0}px`);
  }

  function scrollToReviews(controller, event) {
    if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const target = controller.root.querySelector('.dbq-review-summary') || controller.root;
    if (!target.isConnected) return;
    updateScrollSpacing(controller);
    const documentStyle = getComputedStyle(document.documentElement);
    const targetTop = window.scrollY + target.getBoundingClientRect().top;
    let headerOffset = parseFloat(documentStyle.scrollPaddingTop) || 0;
    const margin = parseFloat(getComputedStyle(target).scrollMarginBlockStart) || 0;
    const header = document.querySelector('x-header[hide-on-scroll]');
    // Predict the header's settled state before starting. The old implementation
    // scrolled with the current header height, then jumped again after scrollend.
    // Do not change the header, its CSS variables, or any other anchor handler.
    if (header && parseFloat(documentStyle.getPropertyValue('--header-is-sticky')) === 1) {
      const visibility = parseFloat(documentStyle.getPropertyValue('--header-is-visible'));
      const currentVisibility = Number.isFinite(visibility) ? visibility : header.classList.contains('is-hidden') ? 0 : 1;
      const headerHeight = parseFloat(documentStyle.getPropertyValue('--header-height')) || header.getBoundingClientRect().height;
      const distance = targetTop - headerOffset - margin - window.scrollY;
      const openMenu = header.querySelector(':where(.header__primary-nav, .header__secondary-nav) [open]');
      const futureVisibility = distance < 0 ? 1 : distance > 100 && !openMenu ? 0 : currentVisibility;
      headerOffset = Math.max(0, headerOffset + (futureVisibility - currentVisibility) * headerHeight);
    }
    // Prevent only this link's default/theme-delegated scroll. One native smooth
    // scroll, with no delayed correction, timer, scrollend listener or second move.
    event.preventDefault();
    window.scrollTo({
      top: Math.max(0, targetTop - headerOffset - margin),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
    });
    document.documentElement.dispatchEvent(new CustomEvent('hashchange:simulate', {
      bubbles: true, detail: { hash: `#${target.id}` }
    }));
  }

  function linkProductRating() {
    const first = controllers.values().next().value;
    if (!first?.root.isConnected) return;
    const info = document.querySelector('.shopify-section--main-product .product-info');
    if (!info) return;
    const native = info.querySelector('[data-block-type="rating"] .rating-badge');
    if (!native) return;
    let link = native;
    if (native.tagName !== 'A') {
      // Keep the exact native markup, SVGs, classes and location. Only the tag
      // becomes a link; the shared theme's other anchor handlers stay untouched.
      link = document.createElement('a');
      [...native.attributes].forEach(attr => link.setAttribute(attr.name, attr.value));
      while (native.firstChild) link.appendChild(native.firstChild);
      native.replaceWith(link);
    }
    const summary = first.root.querySelector('.dbq-review-summary');
    setAttribute(link, 'href', `#${summary?.id || first.root.id}`);
    if (!linkedRatings.has(link)) {
      // The theme's delegated handler respects defaultPrevented. Other links
      // continue to use the theme untouched; only this rating gets its exact offset.
      link.addEventListener('click', event => {
        const controller = linkedRatings.get(link);
        if (controller?.root.isConnected) scrollToReviews(controller, event);
      });
    }
    // Shopify can replace the reviews section without replacing this product
    // link. Refresh its target controller without adding another click handler.
    linkedRatings.set(link, first);
    setAttribute(link, 'data-debacq-rating-link', '');
    info.querySelectorAll('.jdgm-preview-badge, .jdgm-prev-badge').forEach(badge => {
      setAttribute(badge, 'data-debacq-duplicate-badge', '');
      setAttribute(badge, 'aria-hidden', 'true');
      badge.querySelectorAll('a, button, [tabindex]').forEach(control => setAttribute(control, 'tabindex', '-1'));
    });
    if (observedProductInfo !== info) {
      productObserver?.disconnect();
      if (productFrame) { cancelAnimationFrame(productFrame); productFrame = 0; }
      observedProductInfo = info;
      productObserver = new MutationObserver(() => {
        if (productFrame) return;
        productFrame = requestAnimationFrame(() => { productFrame = 0; linkProductRating(); });
      });
      productObserver.observe(info, { childList: true, subtree: true });
    }
  }

  function spacedText(node) {
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    const parts = [];
    while (walker.nextNode()) {
      if (!walker.currentNode.parentElement.closest('script, style, template, .dbq-review-summary')) parts.push(walker.currentNode.nodeValue);
    }
    return parts.join(' ').replace(/[★☆\ue000-\uf8ff]/gu, ' ').replace(/\s+/g, ' ').trim();
  }
  function summaryValues(node) {
    // A review count is an integer, never two numbers to split into a score and
    // a count. In particular, "102 reviews" must not become "1 / 5, 02 reviews".
    const countPattern = /^(0|[1-9]\d*|[1-9]\d{0,2}(?:,\d{3})+|[1-9]\d{0,2}(?:\.\d{3})+|[1-9]\d{0,2}(?:[ \u00a0\u202f]\d{3})+)\s*(reviews?)$/i;
    const scorePattern = /^(?:rated\s+)?([0-5](?:[.,]\d+)?)\s*(?:(?:\/|out\s+of)\s*5(?:[.,]0+)?(?:\s*stars?)?|stars?)?$/i;
    const score = value => {
      const match = String(value ?? '').trim().match(scorePattern);
      const number = match ? Number(match[1].replace(',', '.')) : NaN;
      return Number.isFinite(number) && number >= 0 && number <= 5 ? number : null;
    };
    const parseCount = value => {
      const match = String(value ?? '').trim().replace(/^[([]\s*|\s*[)\]]$/g, '').match(countPattern);
      if (!match) return null;
      const number = Number(match[1].replace(/[, .\u00a0\u202f]/g, ''));
      return Number.isSafeInteger(number) && number >= 0 ? { count: number, label: `${match[1]} ${match[2]}` } : null;
    };
    const text = spacedText(node);
    const declaredRating = score(node.getAttribute('data-average-rating'));
    const declaredCount = node.getAttribute('data-number-of-reviews');
    if (declaredRating !== null && /^(?:0|[1-9]\d*)$/.test(declaredCount || '')) {
      const total = Number(declaredCount);
      if (Number.isSafeInteger(total)) return {
        rating: declaredRating, count: total, label: `${total} ${total === 1 ? 'review' : 'reviews'}`
      };
    }
    // Prefer separately supplied rating attributes or separate score elements.
    // Do not borrow a number from the count or a neighbouring review/filter.
    const scoreNodes = [node, ...node.querySelectorAll('[data-average-rating], [data-rating], [class*="average"], [class*="score"], span')];
    let rating = null;
    for (const source of scoreNodes) {
      rating = score(source.getAttribute('data-average-rating')) ?? score(source.getAttribute('data-rating')) ?? score(spacedText(source));
      if (rating !== null) break;
    }
    const countNodes = [...node.querySelectorAll('span, p, div'), node];
    const counts = countNodes.map(source => parseCount(spacedText(source))).filter(Boolean);
    const count = counts[0];
    if (rating !== null && count) return { rating, ...count };
    if (parseCount(text)) return null; // A standalone count is never a summary.

    // Text-only summaries require an explicit boundary between score and count.
    // The count grammar rejects decimals and malformed grouped integers.
    const combined = text.match(/^(?:rated\s+)?([0-5](?:[.,]\d+)?)\s*(?:(?:\/|out\s+of)\s*5(?:[.,]0+)?(?:\s*stars?)?)?(?:\s+|\s*[([|·]\s*)(?:based\s+on\s+)?(.+?\breviews?)\s*[)\]]?$/i);
    if (!combined) return null;
    rating = score(combined[1]);
    const total = parseCount(combined[2]);
    return rating !== null && total ? { rating, ...total } : null;
  }
  function findSummary(root) {
    // Find the compact rating/count itself. Header wrappers vary between widget
    // versions, so do not require one guessed header class to exist first.
    const acceptable = node => {
      if (node.closest('.dbq-review-summary, [data-debacq-review-probes], .jm-review, .jdgm-rev, .jm-histogram, .jdgm-histogram, .jdgm-write-review-modal')) return false;
      if (node.querySelector('button, select, input, textarea, .jm-action-buttons, .jm-histogram, .jm-reviewer-info')) return false;
      const text = spacedText(node);
      return text.length < 120 && summaryValues(node);
    };
    const selectors = '.jm-average-rating, [class*="average-rating"], .jdgm-rev-widg__summary, [class*="header"][class*="summary"]';
    const candidates = [...root.querySelectorAll(selectors), ...root.querySelectorAll('p, span, div')];
    const matches = candidates.filter(acceptable);
    // The smallest eligible wrapper keeps neighbouring action/filter controls intact.
    return matches.sort((a, b) => a.querySelectorAll('*').length - b.querySelectorAll('*').length)[0] || null;
  }
  function enhanceSummary(controller) {
    const root = controller.root;
    let original = root.querySelector('.dbq-review-summary-original');
    let values = original && summaryValues(original);
    // A replaced, incomplete or previously misidentified source must not leave
    // stale numbers visible. Restore the app's original output until unambiguous.
    if (!values || values.count < 1) {
      original?.classList.remove('dbq-review-summary-original');
      root.querySelector('.dbq-review-summary')?.remove();
      original = findSummary(root);
      values = original && summaryValues(original);
    }
    if (!original || !values || values.count < 1) return;
    let summary = root.querySelector('.dbq-review-summary');
    if (!summary) summary = root.querySelector('[data-debacq-review-summary]')?.content.firstElementChild?.cloneNode(true);
    if (!summary) return;
    const rating = Math.round(values.rating * 10) / 10;
    summary.id = `${root.id}-summary`;
    const value = summary.querySelector('[data-debacq-summary-value]');
    const count = summary.querySelector('[data-debacq-summary-count]');
    if (value.textContent !== String(rating)) value.textContent = String(rating);
    if (count.textContent !== values.label) count.textContent = values.label;
    const stars = summary.querySelector('.rating-badge__stars');
    if (summary.dataset.dbqRating !== String(rating)) {
      [...stars.children].forEach((star, index) => {
        const icon = controller.icons[ratingState(rating, index)];
        if (icon) star.replaceWith(icon.cloneNode(true));
      });
      setAttribute(stars, 'aria-label', `${rating} / 5`);
      summary.dataset.dbqRating = String(rating);
    }
    if (!summary.isConnected || summary.nextElementSibling !== original) original.before(summary);
    original.classList.add('dbq-review-summary-original');
    linkProductRating();
  }

  function iconState(icon) {
    const classes = icon.getAttribute('class') || '';
    if (/(?:--|\s)(?:off|empty|unfilled|unselected)(?:\s|$)/i.test(classes)) return 'empty';
    if (/(?:--|\s)(?:half|half-filled)(?:\s|$)/i.test(classes)) return 'half';
    if (/(?:--|\s)(?:on|full|filled|selected|active)(?:\s|$)/i.test(classes)) return 'full';
    if (icon.tagName.toLowerCase() === 'svg') {
      const path = icon.querySelector('path, polygon');
      if (!path) return null;
      const fill = path.getAttribute('fill') || icon.getAttribute('fill') || getComputedStyle(path).fill;
      if (fill === 'none' || fill === 'transparent' || fill === 'rgba(0, 0, 0, 0)') return 'empty';
      // Outline icons can also be black, filled ring paths. Test their actual
      // centre geometry instead of mistaking a black outline for a full star.
      try {
        const box = icon.viewBox.baseVal;
        if (!box.width || !path.isPointInFill) return null;
        let point = new DOMPoint(box.x + box.width / 2, box.y + box.height * .52);
        const svgMatrix = icon.getCTM(), pathMatrix = path.getCTM();
        if (svgMatrix && pathMatrix) point = point.matrixTransform(svgMatrix).matrixTransform(pathMatrix.inverse());
        return path.isPointInFill(point) ? 'full' : 'empty';
      } catch (_) { return null; }
    }
    const previous = originalGlyphs.get(icon);
    let glyph = normalText(icon);
    if (!glyph) {
      if (previous?.classes === classes) glyph = previous.glyph;
      else {
        // Only inspect pseudo-content when the app changes a source class.
        const old = icon.getAttribute('data-debacq-star');
        icon.removeAttribute('data-debacq-star');
        glyph = getComputedStyle(icon, '::before').content.replace(/^['"]|['"]$/g, '');
        if (old) icon.setAttribute('data-debacq-star', old);
      }
    }
    originalGlyphs.set(icon, { classes, glyph });
    if (/[★\ue000\uf005]/u.test(glyph)) return 'full';
    if (/[☆\ue001\uf006]/u.test(glyph)) return 'empty';
    if (/[\ue002\uf089]/u.test(glyph)) return 'half';
    return null;
  }
  function enhanceStars(surface) {
    const groups = surface.querySelectorAll('.jm-star-rating, .jdgm-rev__rating, .jdgm-write-review-modal__stars');
    groups.forEach(group => {
      const form = !!group.closest('.jdgm-write-review-modal');
      const icons = [...group.querySelectorAll('.jm-star-rating__font-icon, .jdgm-star, svg')]
        .filter(icon => !icon.closest('.dbq-native-control-icon') && !icon.parentElement.closest('.jm-star-rating__font-icon, .jdgm-star'));
      if (icons.length === 5) {
        let row = icons[0].parentElement;
        while (row && row !== group && !icons.every(icon => row.contains(icon))) row = row.parentElement;
        if (row && row.querySelectorAll('button, [role="radio"]').length <= 5) {
          setAttribute(row, 'data-debacq-star-row', form ? 'choices' : 'rating');
          icons.forEach(icon => {
            let item = icon;
            while (item.parentElement !== row && item.parentElement) item = item.parentElement;
            setAttribute(item, 'data-debacq-star-item', '');
          });
        }
      }
      let rating = null;
      if (!form) {
        for (const key of ['data-rating', 'data-score', 'aria-label', 'title']) {
          rating = readNumber(group.getAttribute(key));
          if (rating !== null) break;
        }
        if (rating === null) rating = readNumber(group.closest('[data-rating]')?.getAttribute('data-rating'));
      }
      icons.forEach((icon, index) => {
        const state = rating !== null && icons.length === 5 ? ratingState(rating, index) : iconState(icon);
        if (!state) return; // An unknown app state keeps its original star.
        setAttribute(icon, 'data-debacq-star', state);
        if (form) {
          setAttribute(icon, 'data-debacq-form-star', '');
          const choice = icon.closest('button, [role="radio"], label');
          if (choice && group.contains(choice)) choice.classList.add('dbq-review-rating-choice');
        }
      });
    });
    // Older lightbox output can expose the five font icons without a group.
    surface.querySelectorAll('.jm-star-rating__font-icon:not([data-debacq-star]), .jdgm-star:not([data-debacq-star])').forEach(icon => {
      const state = iconState(icon);
      if (state) setAttribute(icon, 'data-debacq-star', state);
    });
  }

  const PURCHASE_LABEL = 'Verified Buyer';
  const OWN_BADGE = '[data-debacq-purchase-badge]';
  const VENDOR_BADGE = '[class*="verified"], .jdgm-rev__buyer-badge';

  function verificationSources(info) {
    return [...new Set([
      ...info.querySelectorAll(VENDOR_BADGE),
      ...[...info.querySelectorAll('.jm-badge, span')].filter(node =>
        /^(?:verified|verified buyer|verified purchase|verified by judge\.me)$/i.test(normalText(node)) &&
        !node.closest('.jm-review-content__body, .jdgm-rev__body, [data-debacq-purchase-badge]'))
    ])];
  }
  function enhanceVerification(surface) {
    const infos = [...surface.querySelectorAll('.jm-reviewer-info, .jdgm-rev__header')];
    if (surface.matches(PHOTO)) {
      surface.querySelectorAll('.jm-content').forEach(content => {
        if (!content.querySelector('.jm-reviewer-info, .jdgm-rev__header')) infos.push(content);
      });
    }
    infos.forEach(info => {
      const source = verificationSources(info)[0];
      const name = info.querySelector('.jm-reviewer-info__name, .jdgm-rev__author, .jm-content__author, .jm-content__name, [data-debacq-review-name]') ||
        (info.matches('.jm-content') ? source?.previousElementSibling : null);
      if (!name || name.matches(OWN_BADGE)) return;
      setAttribute(name, 'data-debacq-review-name', '');
      // Restore only our suppression while checking the app's current state.
      // This also handles recycled lightbox/review nodes and late verification.
      info.querySelectorAll('[data-debacq-native-verification]').forEach(node => node.removeAttribute('data-debacq-native-verification'));
      const sources = verificationSources(info).filter(node => {
        if (node.matches(OWN_BADGE) || node.closest(OWN_BADGE) || node === name || node.contains(name)) return false;
        const classes = node.getAttribute('class') || '';
        return !/unverified|not-verified|dbq-review-verified/i.test(classes);
      });
      // Storefront label requested by the merchant for every rendered review.
      // No date, ID, rating or app-verification gate; this does not verify orders.
      const existing = [...info.querySelectorAll(OWN_BADGE)];
      // One merchant-owned text node. Judge.me's source DOM and verification
      // metadata remain intact, but never appear as a second visual/AT badge.
      const badge = existing.shift() || document.createElement('span');
      existing.forEach(node => node.remove());
      badge.className = 'dbq-review-verified';
      setAttribute(badge, 'data-debacq-purchase-badge', '');
      if (badge.textContent !== PURCHASE_LABEL) badge.textContent = PURCHASE_LABEL;
      if (!badge.isConnected || badge.previousElementSibling !== name) name.after(badge);
      sources.forEach(node => setAttribute(node, 'data-debacq-native-verification', ''));
    });
  }

  function decorateControl(button, iconName, controller) {
    if (button?.hasAttribute('data-debacq-photo-proxy')) return;
    if (!button || !controller.icons[iconName]) return;
    // Keep the app's actual buttons and the theme SVGs, without the circle
    // surface or its slide-in hover effect. Close remains the plain theme X.
    const photoArrow = iconName === 'previous' || iconName === 'next';
    button.classList.remove('dbq-review-circle', 'circle-button', 'hover:animate-icon-inline');
    button.classList.toggle('dbq-review-photo-arrow', photoArrow);
    button.classList.toggle('dbq-review-icon-button', !photoArrow);
    setAttribute(button, 'data-debacq-control', iconName);
    [...button.children].filter(child => !child.classList.contains('dbq-native-control-icon')).forEach(child => {
      if (child.matches('svg, i, span')) setAttribute(child, 'data-debacq-original-icon', '');
    });
    let holder = button.querySelector('.dbq-native-control-icon');
    if (!holder) {
      // Reuse the original theme arrow path. Only its presentation changes;
      // no duplicate icon or second navigation handler is added.
      holder = photoArrow ? controller.icons[iconName].cloneNode(true) : document.createElement('span');
      holder.classList.add('dbq-native-control-icon');
      holder.setAttribute('aria-hidden', 'true');
      if (!photoArrow) holder.appendChild(controller.icons[iconName].cloneNode(true));
      button.appendChild(holder);
    }
  }
  function themeButton(button, outline = false) {
    button.classList.remove('circle-button', 'dbq-review-circle', 'dbq-review-icon-button', 'dbq-review-photo-arrow', 'hover:animate-icon-inline');
    button.removeAttribute('data-debacq-control');
    button.querySelectorAll('.dbq-native-control-icon').forEach(icon => icon.remove());
    button.querySelectorAll('[data-debacq-original-icon]').forEach(icon => icon.removeAttribute('data-debacq-original-icon'));
    button.classList.add('button', 'dbq-review-button');
    button.classList.toggle('button--outline', outline);
  }
  function enhanceControls(surface, controller) {
    surface.querySelectorAll('.jm-action-buttons__button, .jdgm-write-rev-link, .jdgm-write-review-modal__nav-btn, .jdgm-write-review-modal__submit-btn').forEach(button => {
      themeButton(button, /nav-btn(?:--|-)back\b/.test(button.className));
    });
    surface.querySelectorAll('input.jdgm-write-review-modal__field-input, textarea.jdgm-write-review-modal__field-input').forEach(input => {
      input.classList.add(input.tagName === 'TEXTAREA' ? 'textarea' : 'input', 'dbq-review-input');
    });
    if (surface.matches(FORM)) {
      surface.querySelectorAll('button').forEach(button => {
        const text = normalText(button);
        const textClose = /^(?:close|done)$/i.test(text) && !button.closest('.jdgm-write-review-modal__page--header');
        if (textClose) {
          themeButton(button);
          setAttribute(button, 'data-debacq-text-close', '');
        } else if (button.matches('.jdgm-write-review-modal__close-btn, button[aria-label^="Close" i]') && !button.matches('.dbq-review-button')) {
          decorateControl(button, 'close', controller);
          button.classList.add('dbq-review-form-close');
        }
      });
    }
    if (surface.matches(PHOTO)) {
      surface.querySelectorAll('.jdgm-modal__close, .jdgm-modal__close-btn, .jdgm-modal__close-button, .jdgm-close-ico, .jm-close-button, button[aria-label*="close" i], [role="button"][aria-label*="close" i]').forEach(button => {
        decorateControl(button, 'close', controller);
        button.classList.add('dbq-review-photo-close');
      });
      surface.querySelectorAll('.jm-carousel__prev, .jm-carousel__previous, button[aria-label^="Previous" i]').forEach(button => {
        decorateControl(button, 'previous', controller);
      });
      surface.querySelectorAll('.jm-carousel__next, button[aria-label^="Next" i]').forEach(button => {
        decorateControl(button, 'next', controller);
      });
    }
  }
  function reviewPhotoPanels(surface) {
    // Inner photo carousels can also use .jm-slide. Only the panel that owns
    // review copy is the outer photo/review layout, not an individual photo page.
    return [...surface.querySelectorAll('.jm-slide')].filter(slide => {
      const content = slide.querySelector('.jm-content');
      return content && content.closest('.jm-slide') === slide;
    });
  }
  function isPhotoAuxiliary(node) {
    return node.matches('[role="tablist"], [role="tab"]') || [...node.classList].some(name =>
      // "with-thumbnails" describes a whole gallery, not a thumbnail rail.
      !/(?:^|[-_])(?:with|has)[-_]/i.test(name) &&
      /(?:^|[-_])(?:thumb(?:nail)?s?|avatars?)(?:$|[-_])/i.test(name));
  }
  function insidePhotoAuxiliary(node, boundary) {
    for (; node && node !== boundary; node = node.parentElement) {
      if (isPhotoAuxiliary(node)) return true;
    }
    return false;
  }
  function photoNavigation(surface, panel, media, panels) {
    const buttons = [...surface.querySelectorAll('[data-debacq-control="previous"], [data-debacq-control="next"]')].filter(button =>
      !button.hasAttribute('data-debacq-photo-proxy') && !insidePhotoAuxiliary(button, surface) && !button.closest('.jm-content') &&
      !panels.some(other => other !== panel && other.contains(button)));
    const local = buttons.filter(button => media.contains(button));
    return { photo: local.length ? local : buttons, review: local.length ? buttons.filter(button => !media.contains(button)) : [] };
  }
  function prepareReviewPhotos(surface, controller) {
    if (!surface.matches(PHOTO)) return;
    const excluded = '.jm-content, .jm-reviewer-info, [data-debacq-control]';
    const panels = reviewPhotoPanels(surface);
    const wanted = new Map();
    const mark = (node, name, value = '') => {
      if (!wanted.has(name)) wanted.set(name, new Map());
      wanted.get(name).set(node, value);
    };
    const branchOf = (node, ancestor) => {
      while (node?.parentElement && node.parentElement !== ancestor) node = node.parentElement;
      return node?.parentElement === ancestor ? node : null;
    };
    panels.forEach(slide => {
      const copy = branchOf(slide.querySelector('.jm-content'), slide);
      const images = [...slide.querySelectorAll('img')].filter(image =>
        !image.closest(excluded) && !insidePhotoAuxiliary(image, slide) && !panels.some(other =>
          other !== slide && slide.contains(other) && other.contains(image)));
      if (!images.length) return;
      const media = branchOf(images[0], slide);
      // One shared photo region. Do not combine unrelated columns or resize mixed video.
      if (!media || !copy || media === copy ||
          images.some(image => branchOf(image, slide) !== media) ||
          media.querySelector('video, iframe, model-viewer')) return;
      const multiple = images.length > 1;
      if (multiple && !media.matches('[class*="carousel"], [class*="slider"], [aria-roledescription="carousel"]') &&
          !media.querySelector('[class*="carousel"], [class*="slider"], .jm-slide, [aria-roledescription="slide"], [aria-roledescription="carousel"]') &&
          !images.some(image => image.closest('[hidden], [aria-hidden="true"]'))) return;
      mark(slide, 'data-debacq-photo-slide');
      mark(media, 'data-debacq-photo-media');
      mark(copy, 'data-debacq-photo-copy');
      mark(media, 'data-debacq-photo-frame');
      images.forEach(image => {
        mark(image, 'data-debacq-review-photo');
        // Keep the app's fractional widths, transforms, sequence and selected state.
        if (multiple && images.some(other => other !== image && other.parentElement === image.parentElement)) {
          mark(image, 'data-debacq-photo-item');
        }
        for (let node = image.parentElement; node && node !== media && node !== slide; node = node.parentElement) {
          mark(node, multiple ? 'data-debacq-photo-layer' : 'data-debacq-photo-frame');
        }
      });
      // Locate a rail even inside a nested gallery. A "with-thumbnails" gallery
      // is not itself a rail. Existing thumbnail nodes and handlers stay put.
      const railCandidates = [...media.querySelectorAll('*')].filter(node =>
        isPhotoAuxiliary(node) && node.querySelector('img') && !images.some(image => node.contains(image)));
      const rails = railCandidates.filter(node => !railCandidates.some(other => other !== node && other.contains(node)));
      const thumbnailCount = rails.reduce((count, rail) => count + rail.querySelectorAll('img').length, 0);
      // Count choices within this review, not the number of reviews in the outer
      // lightbox. Multiple thumbnails also cover a reused single main-image node.
      const photoCount = Math.max(images.length, thumbnailCount);
      mark(media, 'data-debacq-photo-count', String(photoCount));
      rails.forEach(rail => {
        mark(rail, 'data-debacq-photo-thumbnails', !multiple && thumbnailCount < 2 ? 'single' : '');
        rail.querySelectorAll('img').forEach(image => {
          mark(image, 'data-debacq-photo-thumb-image');
          const target = image.closest('button, a, [role="button"], [role="tab"]');
          mark(target && target !== rail && rail.contains(target) ? target : image, 'data-debacq-photo-thumb-target');
        });
      });
      const navigation = photoNavigation(surface, slide, media, panels);
      // Only real thumbnail choices reserve a compact, transparent strip.
      // Plain arrows overlay the photo, so they reserve no horizontal gutter.
      if (thumbnailCount > 1) mark(media, 'data-debacq-photo-tools');
      // No visible photo controls need an extended clipping wrapper for a
      // single image. In particular, a hidden one-thumbnail rail reserves nothing.
      (photoCount > 1 ? [...navigation.photo, ...rails] : []).forEach(control => {
        // A nested clipping viewport must also include the reserved lane. Its
        // content height stays unchanged, so the actual photo track still fits.
        for (let node = control.parentElement; node && node !== media && media.contains(node); node = node.parentElement) {
          if (images.some(image => node.contains(image))) mark(node, 'data-debacq-photo-control-host');
        }
      });
      // Absolutely positioned image layers size against a padded containing
      // block differently. Record only those roots; preserve horizontal layout.
      const layers = new Set();
      images.forEach(image => {
        for (let node = image; node && node !== media && !layers.has(node); node = node.parentElement) layers.add(node);
      });
      layers.forEach(node => {
        if (getComputedStyle(node).position === 'absolute') mark(node, 'data-debacq-photo-absolute-layer');
      });
      const shell = slide.parentElement?.closest('.jdgm-modal__container') || branchOf(slide, surface);
      if (shell && surface.contains(shell)) {
        mark(shell, 'data-debacq-photo-shell');
        // Keep native moving tracks inside a bounded, continuous white frame.
        for (let node = slide.parentElement; node && node !== shell; node = node.parentElement) {
          mark(node, 'data-debacq-photo-shell-layer');
        }
      }
    });
    ['data-debacq-photo-slide', 'data-debacq-photo-media', 'data-debacq-photo-copy',
      'data-debacq-review-photo', 'data-debacq-photo-frame', 'data-debacq-photo-shell', 'data-debacq-photo-shell-layer',
      'data-debacq-photo-layer', 'data-debacq-photo-item', 'data-debacq-photo-count', 'data-debacq-photo-tools', 'data-debacq-photo-side-nav',
      'data-debacq-photo-control-host', 'data-debacq-photo-absolute-layer', 'data-debacq-photo-thumbnails',
      'data-debacq-photo-thumb-image', 'data-debacq-photo-thumb-target'].forEach(name => {
      surface.querySelectorAll(`[${name}]`).forEach(node => {
        if (!wanted.get(name)?.has(node)) {
          node.removeAttribute(name);
          if (name === 'data-debacq-photo-thumbnails') {
            ['top', 'left', 'width'].forEach(key => node.style.removeProperty(`--dbq-review-thumbs-${key}`));
          }
        }
      });
      wanted.get(name)?.forEach((value, node) => setAttribute(node, name, value));
    });
    surface.toggleAttribute('data-debacq-photo-viewer', !!wanted.get('data-debacq-photo-slide')?.size);
    surface.querySelectorAll('.dbq-review-photo-header').forEach(header => {
      if (!header.parentElement?.hasAttribute('data-debacq-photo-shell')) {
        header.parentElement?.removeAttribute('data-debacq-review-navigation');
        header.remove();
      }
    });
    wanted.get('data-debacq-photo-shell')?.forEach((_, shell) => {
      let header = [...shell.children].find(child => child.classList.contains('dbq-review-photo-header'));
      if (!header) {
        header = document.createElement('div');
        header.className = 'dbq-review-photo-header';
        header.setAttribute('role', 'heading');
        header.setAttribute('aria-level', '2');
        shell.append(header);
      }
      const label = controller.config.photoHeading || 'Customer photos';
      if (header.textContent !== label) header.textContent = label;
    });
  }
  // A small, stationary control layer. The real buttons remain in Judge.me's
  // framework tree, including when it nests them inside moving photo pages.
  // Forward exactly one click; never move app nodes or implement a second slider.
  const photoControls = new WeakMap();
  function clearPhotoControls(surface) {
    const state = photoControls.get(surface);
    if (!state) return;
    state.entries.forEach(({proxy, inert}, original) => {
      proxy.remove();
      original.removeAttribute('data-debacq-photo-delegated');
      if (!inert) original.removeAttribute('inert');
    });
    state.host.remove();
    photoControls.delete(surface);
  }
  function stationaryPhotoControls(surface, shell, buttons, rect, place, controller, singlePhoto = false) {
    if (!shell || !buttons.length || !rect.height) { clearPhotoControls(surface); return; }
    let state = photoControls.get(surface);
    if (state?.host.parentElement !== shell) { clearPhotoControls(surface); state = null; }
    if (!state) {
      const host = document.createElement('div');
      host.className = 'dbq-review-photo-controls';
      shell.append(host);
      state = {host, entries: new Map()};
      photoControls.set(surface, state);
    }
    const {host, entries} = state;
    place(host, rect.left, rect.top, '--dbq-review-controls');
    setStyle(host, '--dbq-review-controls-width', `${rect.width}px`);
    setStyle(host, '--dbq-review-controls-height', `${rect.height}px`);
    entries.forEach(({proxy, inert}, original) => {
      if (buttons.includes(original)) return;
      proxy.remove();
      original.removeAttribute('data-debacq-photo-delegated');
      if (!inert) original.removeAttribute('inert');
      entries.delete(original);
    });
    buttons.forEach(original => {
      const direction = original.getAttribute('data-debacq-control');
      let entry = entries.get(original);
      if (!entry) {
        const proxy = document.createElement('button');
        proxy.type = 'button';
        proxy.className = 'dbq-review-photo-arrow';
        proxy.setAttribute('data-debacq-photo-proxy', direction);
        const icon = controller.icons[direction]?.cloneNode(true);
        if (icon) { icon.classList.add('dbq-native-control-icon'); icon.setAttribute('aria-hidden', 'true'); proxy.append(icon); }
        proxy.addEventListener('click', event => {
          // This new control is not a Judge.me navigation target. Only the
          // forwarded original click should reach the app's delegated listener.
          event.stopPropagation();
          if (original.isConnected && !proxy.disabled && !proxy.hidden && !original.matches(':disabled, [aria-disabled="true"]')) original.click();
        });
        entry = {proxy, inert: original.hasAttribute('inert')};
        entries.set(original, entry);
        host.append(proxy);
      }
      setAttribute(original, 'data-debacq-photo-delegated', '');
      setAttribute(original, 'inert', '');
      const {proxy} = entry;
      const style = getComputedStyle(original);
      // A single photograph has nothing to advance to. Keep native controls
      // delegated/inert, but expose no empty or disabled photo-arrow targets.
      if (singlePhoto && proxy === document.activeElement) surface.querySelector('.dbq-review-photo-close')?.focus({preventScroll: true});
      proxy.hidden = singlePhoto || !!original.closest('[hidden], [aria-hidden="true"]') || style.display === 'none' || style.visibility === 'hidden';
      proxy.disabled = original.matches(':disabled, [disabled], [aria-disabled="true"]');
      setAttribute(proxy, 'aria-label', original.getAttribute('aria-label') || (direction === 'previous' ? 'Previous photograph' : 'Next photograph'));
    });
  }
  function positionPhotoClose(surface, controller) {
    if (!surface.matches(PHOTO)) return;
    // On very short/enlarged-text screens, scroll the whole viewer instead of
    // crushing the photograph or covering it with a pinned close control.
    const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    surface.toggleAttribute('data-debacq-photo-scroll', surface.hasAttribute('data-debacq-photo-viewer') && innerHeight < Math.max(480, rootSize * 22.5));
    let panel = null, largestArea = 0;
    const panels = reviewPhotoPanels(surface);
    panels.forEach(slide => {
      if (slide.closest('[hidden], [aria-hidden="true"]') || getComputedStyle(slide).visibility === 'hidden') return;
      const box = slide.getBoundingClientRect();
      const shell = slide.closest('[data-debacq-photo-shell]')?.getBoundingClientRect();
      const area = Math.max(0, Math.min(box.right, shell?.right ?? innerWidth, innerWidth) - Math.max(box.left, shell?.left ?? 0, 0)) *
        Math.max(0, Math.min(box.bottom, innerHeight) - Math.max(box.top, 0));
      if (area > largestArea) { largestArea = area; panel = slide; }
    });
    panel ||= [...surface.querySelectorAll('.jdgm-modal__container')].pop();
    const mediaNode = panel?.hasAttribute('data-debacq-photo-slide') && panel.querySelector('[data-debacq-photo-media]');
    // Compact single/no-thumbnail reviews should not turn a removed thumbnail
    // row into extra empty height in the review column. Re-evaluate on reuse.
    surface.toggleAttribute('data-debacq-photo-no-thumbnails', !!mediaNode && !mediaNode.hasAttribute('data-debacq-photo-tools'));
    const anchored = new Set();
    const clearUnused = () => surface.querySelectorAll('[data-debacq-photo-nav]').forEach(button => {
      if (anchored.has(button)) return;
      button.removeAttribute('data-debacq-photo-nav');
      button.style.removeProperty('--dbq-review-nav-top');
      button.style.removeProperty('--dbq-review-nav-left');
    });
    if (!panel?.getClientRects().length) { clearUnused(); clearPhotoControls(surface); return; }
    const box = panel.getBoundingClientRect();
    if (!box.width || !box.height) { clearUnused(); clearPhotoControls(surface); return; }
    const place = (node, left, top, prefix) => {
      if (!node) return;
      // Preserve the app's DOM, click handler and offset-parent coordinate system.
      const parent = node.offsetParent;
      const origin = parent?.getBoundingClientRect() || { top: 0, left: 0 };
      setStyle(node, `${prefix}-left`, `${Math.round(left - origin.left - (parent?.clientLeft || 0) + (parent?.scrollLeft || 0))}px`);
      setStyle(node, `${prefix}-top`, `${Math.round(top - origin.top - (parent?.clientTop || 0) + (parent?.scrollTop || 0))}px`);
    };
    const close = surface.querySelector('.dbq-review-photo-close');
    const header = parseFloat(getComputedStyle(panel).paddingTop) || 0;
    const closeHeight = close?.getBoundingClientRect().height || 44;
    // The same slim white Close header is used on both layouts.
    const headerTop = box.top + (header ? Math.max(0, (header - closeHeight) / 2) : 8);
    place(close, box.right - (close?.getBoundingClientRect().width || 44) - 8, headerTop, '--dbq-review-close');
    if (!mediaNode || !mediaNode.getClientRects().length) { clearUnused(); clearPhotoControls(surface); return; }
    const media = mediaNode.getBoundingClientRect();
    if (!media.height) { clearUnused(); clearPhotoControls(surface); return; }
    const lane = parseFloat(getComputedStyle(mediaNode).paddingBottom) || 0;
    const navigation = photoNavigation(surface, panel, mediaNode, panels);
    const anchor = (button, left, top, kind) => {
      setAttribute(button, 'data-debacq-photo-nav', kind);
      anchored.add(button);
      place(button, left, top, '--dbq-review-nav');
    };
    // Anchor to the stationary media viewport, never to a fitted image or
    // moving track. Changing photo proportions cannot make the arrows jump.
    const shell = panel.closest('[data-debacq-photo-shell]');
    stationaryPhotoControls(surface, shell, navigation.photo, {
      left: media.left, top: media.top, width: media.width,
      height: Math.max(0, media.height - lane)
    }, place, controller, mediaNode.getAttribute('data-debacq-photo-count') === '1');
    // Separate between-review controls remain app-owned. Keep them at the
    // right of the slim header, before Close and away from the heading text.
    const reviewWidth = navigation.review.reduce((sum, button) => sum + (button.getBoundingClientRect().width || 44) + 4, 0);
    shell?.toggleAttribute('data-debacq-review-navigation', !!navigation.review.length);
    let reviewLeft = box.right - (close?.getBoundingClientRect().width || 44) - 16 - reviewWidth;
    navigation.review.forEach(button => {
      const size = button.getBoundingClientRect();
      anchor(button, reviewLeft, headerTop, 'review');
      reviewLeft += (size.width || 44) + 4;
    });
    mediaNode.querySelectorAll('[data-debacq-photo-thumbnails]').forEach(rail => {
      if (rail.getAttribute('data-debacq-photo-thumbnails') === 'single') return;
      const height = rail.getBoundingClientRect().height || 44;
      setStyle(rail, '--dbq-review-thumbs-width', `${Math.max(0, media.width - 16)}px`);
      place(rail, media.left + 8,
        media.bottom - lane + Math.max(0, (lane - height) / 2), '--dbq-review-thumbs');
    });
    clearUnused();
  }
  function suppressReviewSource(surface) {
    // Clear only our own markers first: Judge.me can recycle nodes when paging
    // or changing photos, so a wrapper that gains real content must reappear.
    surface.querySelectorAll('[data-debacq-source-note]').forEach(node => node.removeAttribute('data-debacq-source-note'));
    const body = '.jdgm-rev__body, .jm-review-content__body, .jm-review-content__title, .jm-review-content__body-content, .jdgm-review-content__body-content';
    const boundary = '.jm-review, .jdgm-rev, .jm-review-content, .jm-content, .jdgm-rev__content, [data-debacq-review-surface]';
    const useful = 'img, video, audio, iframe, input, textarea, select, button, a[href], [role="button"], [role="checkbox"], [role="link"], [tabindex]';
    surface.querySelectorAll('.jm-text, [class*="review-source"], .jdgm-rev__source, .jm-review-content__source').forEach(node => {
      if (node.closest(body) || node.matches(boundary)) return;
      const text = normalText(node);
      if (!/^review (?:written|collected) (?:in|on) (?:the )?shop (?:app|pay)\.?$/i.test(text)) return;
      // Hide the entire source-only wrapper, not just its text. This removes
      // the wrapper's padding/min-height and the flex/grid gap it occupied.
      // Stop before review content, reviewer details, media or action controls.
      let wrapper = node;
      while (wrapper.parentElement && wrapper.parentElement !== surface) {
        const parent = wrapper.parentElement;
        if (parent.matches(boundary) || parent.matches(body) || normalText(parent) !== text) break;
        const otherContent = [...parent.querySelectorAll(useful)].some(child => !node.contains(child));
        if (otherContent) break;
        wrapper = parent;
      }
      setAttribute(wrapper, 'data-debacq-source-note', '');
    });
  }

  function enhance(surface, controller) {
    enhanceControls(surface, controller);
    enhanceStars(surface);
    enhanceVerification(surface);
    suppressReviewSource(surface);
    prepareReviewPhotos(surface, controller);
    positionPhotoClose(surface, controller);
    if (surface === controller.root) enhanceSummary(controller);
  }
  function watchSurface(surface, controller) {
    if (surfaces.has(surface)) return;
    setAttribute(surface, 'data-debacq-review-surface', '');
    if (surface.matches(MODAL) && controller.config.modalScheme) {
      // Apply palette variables, not the generic color-scheme background: the
      // full-screen overlay must remain transparent over the storefront.
      surface.classList.add(`color-scheme--${controller.config.modalScheme}`);
    }
    nativeVariables(controller, surface);
    let frame = 0;
    let geometryObserver;
    let disposed = false;
    const events = new AbortController();
    const schedule = () => {
      if (frame || disposed) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (!surface.isConnected || !controller.root.isConnected) {
          dispose();
          if (surfaces.get(surface)?.controller === controller) surfaces.delete(surface);
          return;
        }
        observer.disconnect();
        enhance(surface, controller);
        observe();
      });
    };
    const observer = new MutationObserver(schedule);
    const dispose = () => {
      disposed = true;
      observer.disconnect();
      geometryObserver?.disconnect();
      events.abort();
      clearPhotoControls(surface);
      if (frame) { cancelAnimationFrame(frame); frame = 0; }
    };
    const observe = () => observer.observe(surface, {
      childList: true, subtree: true, characterData: true, attributes: true,
      attributeFilter: ['class', 'style', 'aria-checked', 'aria-selected', 'aria-label', 'aria-valuenow', 'data-rating', 'data-average-rating', 'data-number-of-reviews', 'data-score', 'data-value', 'fill', 'd', 'checked', 'data-verified-buyer', 'data-review-date', 'data-review-id', 'data-review-uuid', 'datetime', 'hidden', ...(surface.matches(PHOTO) ? ['aria-hidden', 'src', 'srcset', 'disabled', 'aria-disabled'] : [])]
    });
    enhance(surface, controller);
    observe();
    if (surface.matches('.jdgm-write-review-modal')) {
      ['pointerover', 'pointerout', 'change', 'click', 'keydown'].forEach(type => {
        surface.addEventListener(type, event => {
          if (event.target.closest?.('.jdgm-write-review-modal__stars')) schedule();
        }, { passive: true, signal: events.signal });
      });
    }
    if (surface.matches(PHOTO)) {
      // Reuse the surface's existing frame scheduler. No global scroll listener
      // or polling, and all handlers are removed when the app removes the viewer.
      ['scroll', 'transitionend', 'animationend', 'load'].forEach(type => {
        surface.addEventListener(type, schedule, { capture: true, passive: true, signal: events.signal });
      });
    }
    if (surface.matches(PHOTO) && 'ResizeObserver' in window) {
      geometryObserver = new ResizeObserver(schedule);
      geometryObserver.observe(surface);
      const slide = surface.querySelector('.jm-slide');
      if (slide) geometryObserver.observe(slide);
    }
    surfaces.set(surface, { controller, dispose });
  }
  function attachModals(node = document.body) {
    const controller = controllers.values().next().value;
    if (!controller) return;
    selectIncluding(node, MODAL).forEach(modal => watchSurface(modal, controller));
  }
  function scan(scope = document) {
    selectIncluding(scope, ROOT).forEach(root => {
      if (controllers.has(root)) return;
      const config = readConfig(root);
      const iconTemplate = root.querySelector('[data-debacq-review-icons]');
      if (!config || !iconTemplate) return;
      const icons = {};
      iconTemplate.content.querySelectorAll('[data-icon]').forEach(holder => {
        const state = holder.dataset.icon;
        icons[state] = seamlessReviewStar(holder.querySelector('svg'), state);
      });
      const controller = { root, config, icons };
      controllers.set(root, controller);
      watchSurface(root, controller);
    });
    linkProductRating();
    attachModals();
  }
  function prune() {
    controllers.forEach((controller, root) => { if (!root.isConnected) controllers.delete(root); });
    surfaces.forEach((state, surface) => {
      if (!surface.isConnected || !state.controller.root.isConnected) { state.dispose(); surfaces.delete(surface); }
    });
    if (productObserver && (!controllers.size || !observedProductInfo?.isConnected)) {
      productObserver.disconnect();
      productObserver = null;
      observedProductInfo = null;
      if (productFrame) { cancelAnimationFrame(productFrame); productFrame = 0; }
    }
  }
  window.debacqJudgeTheme = { scan };
  scan();
  // Observe added portal roots, including a portal mounted inside an app wrapper.
  // Inspect only added elements, never rescan the whole document on each mutation.
  new MutationObserver(records => {
    prune();
    records.forEach(record => record.addedNodes.forEach(node => { if (node.nodeType === 1) attachModals(node); }));
  }).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', event => { prune(); scan(event.target); });
  document.addEventListener('shopify:section:unload', prune);
  let resizeFrame = 0;
  window.addEventListener('resize', () => {
    if (resizeFrame) return;
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = 0;
      surfaces.forEach((state, surface) => { nativeVariables(state.controller, surface); positionPhotoClose(surface, state.controller); });
    });
  }, { passive: true });
})();
